import { objectTemplateSchemaSelector } from "containers/Resources/Resources.selectors";
import { getRecordLogLevelList } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/constants";
import {
    getFilterStorageKey,
    writeStoredFilter,
} from "containers/ExecutionFilter/filterStorage";
import { ContainerType, RecordType } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getWorkspaceLogsLink } from "utils/RouterManager/utils";
import {
    capitalizeFirstLetterRestLowercase,
    filterRecordList,
    getListFromGraphql,
} from "utils/utils";

// Container types (besides "workspace") that have a "<Container>ItemType" enum
// in the GraphQL schema. "workspace" is handled separately (it uses RecordType).
const CONTAINER_ITEM_ENUM_TYPES = new Set<string>(["project", "bundle"]);

const getResourceNameLabel = (resourceList: RecordType[]) =>
    resourceList.map((resource) => {
        const resourceName = resource.replaceAll("_", " ");
        return {
            resourceName,
            label: capitalizeFirstLetterRestLowercase(resourceName),
        };
    });

const getResourceList = (filteredResources: RecordType[]) =>
    getResourceNameLabel([...filteredResources].sort());

export const getRecordList = (containerType: Lowercase<ContainerType>) => {
    if (!containerType) {
        return {
            recordList: [],
            recordNameList: [],
        };
    }

    let listFromGraphql: RecordType[] = [];
    if (containerType === "workspace") {
        listFromGraphql = getListFromGraphql("RecordType");
    } else if (CONTAINER_ITEM_ENUM_TYPES.has(containerType)) {
        // Only real container types (project/bundle) have a "<Container>ItemType"
        // GraphQL enum. The container-logs route accepts ANY :type, so a
        // non-container record type (custom_object, flow, connector, …) would
        // look up a non-existent enum and crash. Fall through with an empty list
        // → the log filter then offers just the record type itself.
        listFromGraphql = getListFromGraphql(
            `${capitalizeFirstLetterRestLowercase(containerType)}ItemType`
        );
    }

    const filteredListFromGraphql = filterRecordList(listFromGraphql);

    const recordListWithContainerType = filteredListFromGraphql.some(
        (type) => type === containerType.toUpperCase()
    )
        ? filteredListFromGraphql
        : [...filteredListFromGraphql, containerType.toUpperCase()];
    const recordTypeWithLabelList = getResourceNameLabel(recordListWithContainerType);

    const sortedRecordTypeList = getResourceList(recordListWithContainerType);
    const recordNameList = sortedRecordTypeList.map((resource) => resource.resourceName);

    return {
        recordList: recordTypeWithLabelList,
        recordNameList,
    };
};

export const getIdFilter = (id) => ({ id: { field: "record_id", op: "eq", value: id } });

export const useDefaultObjectTemplate = (objectTemplateId) => {
    const objectTemplateSchemaList = useSelector(objectTemplateSchemaSelector, isEqual);
    const defaultObjectTemplate = useMemo(
        () => objectTemplateSchemaList.find(({ id }) => id === objectTemplateId),
        [objectTemplateId, objectTemplateSchemaList]
    );
    const defaultObjectTemplateSchema = useMemo(
        () => defaultObjectTemplate?.schema ?? {},
        [defaultObjectTemplate?.schema]
    );
    // The object_template schema list is populated by the websocket full-state
    // push, which arrives shortly after the record screen mounts. Until it does,
    // the list is empty and every custom object resolves to an empty schema —
    // which SchemaForm would otherwise render as the raw code editor before
    // flipping to the form ("codeview flash"). Treat an empty list (while we
    // have a template id to resolve) as "schema still loading" so the value
    // display can show a placeholder instead. Once the push arrives the list is
    // non-empty (it is delivered atomically as a single array), so a genuinely
    // schema-less template (0 attributes) is correctly NOT flagged as loading.
    const isObjectTemplateSchemaLoading = useMemo(
        () => Boolean(objectTemplateId) && objectTemplateSchemaList.length === 0,
        [objectTemplateId, objectTemplateSchemaList]
    );
    return {
        defaultObjectTemplateSchema,
        defaultObjectTemplate,
        isObjectTemplateSchemaLoading,
    };
};
export const getWorkspaceLogLinkBySubject = (workspaceId: string, subject: string) => {
    const filterStr = JSON.stringify({
        and: [
            {
                field: "recordLogType",
                op: "in",
                value: getRecordList("workspace").recordNameList,
            },
            {
                field: "level",
                op: "in",
                value: getRecordLogLevelList(),
            },
            {
                field: "subjectOrData",
                op: "like",
                value: subject,
            },
        ],
    });
    writeStoredFilter(
        getFilterStorageKey({
            page: "log-filter",
            recordType: "workspace",
            recordId: workspaceId,
        }),
        { filter: filterStr }
    );
    return getWorkspaceLogsLink(workspaceId);
};
